import { Api, ArrayUtil, DOMUtil, FileUtil, HorizontalAlignment, Logger } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../../components/button/Button";
import { ButtonVariant } from "../../components/button/ButtonVariant";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { HorizontalSpacer } from "../../page/HorizontalSpacer";
import { Image } from "../../components/image/Image";
import { ImageProps } from "../../components/image/ImageProps";
import { ImageRetrieverInterface } from "./ImageRetrieverInterface";
import { ImageType } from "../../components/image/ImageType";
import { ImageViewerOpenButtons } from "./ImageViewerOpenButtons";
import { ImageViewerValues } from "./ImageViewerValues";
import { Label } from "../../components/label/Label";
import { Layout } from "../../components/layout/Layout";
import { LayoutProps } from "../../components/layout/LayoutProps";
import { MainPageAccessor } from "../../base/MainPageAccessor";
import { OpenType } from "../openButtons/OpenType";
import { Overlay } from "../../page/Overlay";
import { OverlayProps } from "../../page/OverlayProps";
import { Panel } from "../../components/panel/Panel";
import { ReflectiveSnackbars } from "../../base/ReflectiveSnackbars";
import { EmailDialog } from "../dialogs/EmailDialog";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";

export class ImageViewer extends Layout {
    public static imageRetriever: ImageRetrieverInterface;
    private static logger: Logger;
    readonly PROVIDED_IMAGE_KEY = "imageViewerProvidedValues";
    private _viewerValues: ImageViewerValues;
    private buttonClose: Button;
    private buttonEmail: Button;
    private buttonDownload: Button;
    private image: Image;
    private labelTitle: Label;
    private openButtons: ImageViewerOpenButtons;
    private overlay: Overlay;
    private panelHeaderCenter: Panel;
    private panelHeaderRight: Panel;
    private panelPageControls: Panel;
    private panelZoomControls: Panel;
    private _imageId: string;
    private _fileName: string;

    // LayoutProps have to come first for when we initialize the Layout via the Router
    constructor(props: Partial<LayoutProps>, imageValues?: ImageViewerValues) {
        super({ fillHeight: true, fillRow: true, ...props, needsServerLayout: false });
        const panelHeader = this.createHeaderPanel(imageValues);
        this.add(panelHeader);
        this.setProps({ padding: 0, ...props });
        this.viewerValues = imageValues;
    }

    override async onLoad() {
        if (this.image?.isEmpty() !== false) {
            await this.lookupImage();
        }

        this.addLayoutLoadListener(() => {
            // If the image viewer is being displayed as the main page in the router, we don't need the close button.
            if (this === ArrayUtil.getFirstElement(MainPageAccessor.router.components)) {
                this.removeCloseButton();
            }
        });
    }

    private set viewerValues(value: ImageViewerValues) {
        this.log.debug("Provided imageValues: %o", value);
        this._viewerValues = value;
        this.createImage(value?.fileName);
        if (this.image) {
            this.image.imageBytes = value?.imageBase64;
        }
        this.syncHeader();
    }

    private get viewerValues(): ImageViewerValues | null {
        return this._viewerValues;
    }

    private syncHeader() {
        this.title = this.viewerValues?.title ?? this.viewerValues?.fileName ?? "Image";
        this.syncButtons();
        this.syncPageControls();
        this.syncZoomControls();
    }

    private async lookupImage() {
        // When opened in a new tab/window, imageId and fileName are populated from URL query parameters by the Router.
        if (this._imageId && this._fileName) {
            this.viewerValues = await ImageViewer.imageRetriever.get(this._imageId, this._fileName);
            delete this._imageId;
            delete this._fileName;
        }
    }

    get currentValues(): ImageViewerValues {
        return new ImageViewerValues(this.imageId, this.imageBase64, this.fileName, this.title);
    }

    private createHeaderPanel(imageValues: ImageViewerValues): Panel {
        this.createTitleLabel();
        const panelHeaderLeft = Panel.createNoMarginNoPaddingPanel({ fillRow: true, rowBreakDefault: false });
        panelHeaderLeft.add(this.labelTitle);

        this.panelHeaderCenter = Panel.createNoMarginNoPaddingPanel({
            fillRow: true,
            rowBreakDefault: false,
            align: HorizontalAlignment.CENTER
        });
        this.createPageControlsPanel();
        this.panelHeaderCenter.add(this.panelPageControls);

        this.panelHeaderRight = Panel.createNoMarginNoPaddingPanel({ fillRow: true, rowBreakDefault: false });
        this.createEmailButton();
        this.createDownloadButton();
        this.createOpenButtons();
        this.createCloseButton();
        this.createZoomControlsPanel();
        this.panelHeaderRight.add(
            new HorizontalSpacer(),
            this.panelZoomControls,
            ...(imageValues?.allowEmail ? [this.buttonEmail] : []),
            this.buttonDownload,
            ...this.openButtons.get(),
            this.buttonClose
        );

        const panelHeader = new Panel({
            fillRow: true,
            themeKey: "dialog.header",
            backgroundColor: "primary.darker",
            color: "primary.reverse",
            rowBreakDefault: false
        });
        panelHeader.add(panelHeaderLeft, this.panelHeaderCenter, this.panelHeaderRight);
        return panelHeader;
    }

    private createTitleLabel() {
        this.labelTitle = new Label({ paddingRight: 12, fillRow: true, rowBreak: false });
    }

    private createEmailButton() {
        this.buttonEmail = new Button({ variant: ButtonVariant.round, tooltip: "Email this image", imageName: ImageName.send, rowBreak: false })
        this.buttonEmail.addClickListener(() => {
            const defaultSubject = "Image: " + this.fileName;
            const defaultBody = "Please find the attached image: " + this.fileName;
            const panel = new EmailDialog({
                defaultSubject: defaultSubject,
                defaultMessage: defaultBody
            });
            const dialogProps = {
                yesButtonCaption: "Send",
                noButtonCaption: "Cancel"
            };
            ReflectiveDialogs.showYesNo(panel, "Send Email", dialogProps).then(async (result) => {
                this.log.debug("Email dialog result: ", result);
                if (result === true) {
                    this.log.debug("Emailing image with values: ", this.currentValues);
                    try {
                        await Api.post("common/imaging/email-image", {
                            body: panel.messageTextbox.text, cc_user: panel.switchCopyToSelf.checked, file_name: this.fileName,
                            id: this.imageId, recipient: panel.recipientTextbox.text, subject: panel.subjectTextbox.text
                        });
                        ReflectiveSnackbars.showSnackbar("Emailed " + this.fileName);
                    } catch (error: any) {
                        ReflectiveSnackbars.showSnackbar("Failed to send email: " + error.message);
                    }
                }
            });
        });
    }

    private createDownloadButton() {
        this.buttonDownload = new Button({
            variant: ButtonVariant.round,
            tooltip: "Download this image",
            imageName: ImageName.download,
            rowBreak: false
        });
        this.buttonDownload.addClickListener(() => this.downloadImage());
    }

    private async downloadImage() {
        // If the image being viewed is a TIFF, request it in PDF form and download that file when it's ready.
        // Otherwise, download the file as-is (the bytes we are viewing in the ImageViewer).
        if (this.image.imageType === ImageType.TIFF && ImageViewer.imageRetriever != null) {
            const downloadStartSnackbar = ReflectiveSnackbars.showDownloadSnackbar("Requesting Download",
                "The file will be downloaded when available.", { id: "imageDownloadStart", persist: true });
            const fileNameNoExt = this.fileName.substring(0, this.fileName.lastIndexOf("."));
            let imageViewerValues: ImageViewerValues;
            try {
                imageViewerValues = await ImageViewer.imageRetriever.getAsPdf(this.imageId, fileNameNoExt);
            } catch (error) {
                this.log.error("Unable to get document as PDF, will download document as-is", error);
                imageViewerValues = null;
            }
            if (imageViewerValues != null) {
                FileUtil.downloadBase64AsFile(imageViewerValues.imageBase64, imageViewerValues.fileName);
                downloadStartSnackbar?.dismiss();
                ReflectiveSnackbars.showSnackbar("Downloaded " + imageViewerValues.fileName);
            } else {
                FileUtil.downloadBase64AsFile(this.imageBase64, this.fileName);
                downloadStartSnackbar?.dismiss();
                ReflectiveSnackbars.showSnackbar("Downloaded " + this.fileName);
            }
        } else {
            FileUtil.downloadBase64AsFile(this.imageBase64, this.fileName);
            ReflectiveSnackbars.showSnackbar("Downloaded " + this.fileName);
        }
    }

    private createOpenButtons() {
        const allowedTypes = [OpenType.NEW_TAB_WITH_SWITCH, OpenType.NEW_WINDOW];
        this.openButtons = new ImageViewerOpenButtons(this, () => this.currentValues, { allowedTypes });
    }

    private createCloseButton() {
        this.buttonClose = new Button({
            variant: ButtonVariant.round,
            tooltip: "Close this image preview",
            imageName: ImageName.x,
            cancel: true
        })
        this.buttonClose.addClickListener(() => Overlay.hideOverlay(this.overlay));
    }

    private removeCloseButton() {
        this.panelHeaderRight.remove(this.buttonClose);
    }

    private createImage(fileName: string) {
        if (this.image?.imageType !== Image.getImageTypeForFileName(fileName)) {
            this.panelPageControls.removeAll();
            this.panelZoomControls.removeAll();
            if (this.image != null && this.contains(this.image)) {
                this.remove(this.image);
            }
            const imageProps: Partial<ImageProps> = { fillRow: true, fillHeight: true, preserveAspectRatio: true };
            this.image = Image.createFileBasedImage(imageProps, fileName);
            this.add(this.image);
        }
    }

    private syncButtons() {
        const imagePresent = this.image?.isEmpty() === false;
        this.buttonEmail.enabled = imagePresent;
        this.buttonDownload.enabled = imagePresent;
        this.openButtons.enableAll(imagePresent);
    }

    private createPageControlsPanel() {
        this.panelPageControls = Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
    }

    private syncPageControls() {
        this.syncControlsPanel(this.panelPageControls, this.image?.pageControls);
    }

    private createZoomControlsPanel() {
        this.panelZoomControls = Panel.createNoMarginNoPaddingPanel({ marginRight: 20, rowBreak: false });
    }

    private syncZoomControls() {
        this.syncControlsPanel(this.panelZoomControls, this.image?.zoomControls);
    }

    private syncControlsPanel(controlsPanel: Panel, newControls: Panel) {
        controlsPanel.removeAll();
        if (newControls != null) {
            controlsPanel.add(newControls);
        }
    }

    private set imageId(value: string) {
        this._imageId = value;
    }

    private get imageId(): string {
        return this._imageId ?? this.viewerValues.id;
    }

    private set fileName(value: string) {
        this._fileName = value;
    }

    private get fileName(): string {
        return this._fileName ?? this.viewerValues.fileName;
    }

    private get imageBase64(): string {
        return this.viewerValues.imageBase64;
    }

    public get title(): string {
        return this.labelTitle.text;
    }

    public set title(value: string) {
        this.labelTitle.text = value;
    }

    public showInOverlay(overlayProps: Partial<OverlayProps>, sizeImageToOverlay: boolean = true) {
        this.overlay = Overlay.showInOverlay(this, {
            style: {
                width: "94%",
                height: "94%",
                position: "absolute",
                left: "3%",
                top: "3%",
                backgroundColor: "#eee",
                borderRadius: "4px"
            },
            coverPageHeader: false,
            ...overlayProps
        });

        if (sizeImageToOverlay === true) {
            // Ensure the image doesn't overrun the bounds of the overlay
            this.sizeImageToOverlay();
            this.overlay.getOverlayContent().addResizeListener(() => {
                this.sizeImageToOverlay();
            });
        }
    }

    private sizeImageToOverlay() {
        const overlayContentElement = this.overlay.getOverlayContent()._element;
        this.height = DOMUtil.getElementHeightString(overlayContentElement);
        this.width = DOMUtil.getElementWidthString(overlayContentElement);
    }

    public static viewOrDownload(imageViewerValues: ImageViewerValues): void {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true)
            ImageViewer.viewImage(imageViewerValues);
        else {
            FileUtil.downloadBase64AsFile(imageViewerValues.imageBase64, imageViewerValues.fileName);
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed; the file has been downloaded as " +
                imageViewerValues.fileName, { persist: true });
        }
    }

    public static eligibleToView(fileName: string) {
        const fileNameLower = fileName.toLowerCase();
        return fileNameLower.endsWith(".jpg") || fileNameLower.endsWith(".jpeg") || fileNameLower.endsWith(".png") ||
            fileNameLower.endsWith(".bmp") || fileNameLower.endsWith(".gif") || fileNameLower.endsWith(".tiff") ||
            fileNameLower.endsWith(".pdf");
    }

    public static viewImage(imageViewerValues: ImageViewerValues, overlayProps?: Partial<OverlayProps>,
        sizeImageToOverlay: boolean = true) {
        if (ImageViewer.eligibleToView(imageViewerValues.fileName) === true) {
            const imageViewer = new ImageViewer({ borderShadow: true }, imageViewerValues);
            imageViewer.showInOverlay(overlayProps, sizeImageToOverlay);
        } else {
            ReflectiveSnackbars.showSnackbar("This type of file cannot be previewed");
        }
    }

    private get log(): Logger {
        if (ImageViewer.logger == null) {
            ImageViewer.logger = DesignableObjectLogManager.getLogger("components.page.imageViewer.ImageViewer");
        }
        return ImageViewer.logger;
    }
}
